/**
 * One set of status colours, learned once.
 *
 * ## What it replaces
 *
 * Across the console a person could meet "Needs a landlord", "Waiting",
 * "breached", "up", "active", "new", "built-in" and "required" — some as
 * pills, some as plain text, some coloured, some not, and the same meaning
 * wearing different colours on different screens. Every screen taught its own
 * palette, so none of them taught anything.
 *
 * ## The five, and why five
 *
 * The set is deliberately small. A palette with a colour per status is a
 * palette nobody remembers; these are the only distinctions that change what
 * somebody does next:
 *
 *   `neutral`    a fact, no action implied — built-in, draft, archived
 *   `waiting`    working as intended, not finished — pending, queued
 *   `attention`  something is needed from a person — needs a landlord, required
 *   `bad`        it failed or it is broken — breached, failed, down
 *   `good`       it worked, it is done — paid, sent, active, up
 *
 * ## Why the word always appears
 *
 * Colour is never the only carrier. Around one man in twelve cannot separate
 * these reliably, and the same pills are read in print and in a screenshot
 * pasted into a chat where the colour survives but the legend does not.
 */
export type StatusTone = 'neutral' | 'waiting' | 'attention' | 'bad' | 'good';

/**
 * Built from the theme tokens rather than raw colours, so a change to the
 * palette reaches these and a dark mode would not have to hunt them down.
 */
const TONES: Record<StatusTone, string> = {
  neutral: 'border-border bg-muted text-muted-foreground',
  waiting: 'border-border bg-muted text-foreground',
  attention: 'border-accent-300 bg-accent-50 text-accent-900',
  bad: 'border-danger-300 bg-danger-50 text-danger-700',
  good: 'border-secondary-300 bg-secondary-50 text-secondary-900',
};

export function StatusPill({
  tone = 'neutral',
  children,
  title,
}: {
  tone?: StatusTone;
  children: React.ReactNode;
  /** What this state means, where the word alone is not self-explanatory. */
  title?: string;
}) {
  return (
    <span
      className={`label-mono inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-xs ${TONES[tone]}`}
      title={title}
    >
      {children}
    </span>
  );
}

/**
 * The tone for a status word, so two screens showing the same state cannot
 * disagree about what colour it is.
 *
 * Unknown words get `neutral` rather than a guess. A wrong colour is worse
 * than no colour: grey says "this is a fact I have no opinion about", which is
 * true, whereas a hopeful green says something the system does not know.
 */
const KNOWN: Record<string, StatusTone> = {
  /* Good — it worked, or it is running. */
  paid: 'good',
  sent: 'good',
  delivered: 'good',
  active: 'good',
  up: 'good',
  ok: 'good',
  done: 'good',
  approved: 'good',
  signed: 'good',
  configured: 'good',

  /* Waiting — working as intended, not finished. */
  waiting: 'waiting',
  pending: 'waiting',
  queued: 'waiting',
  'in flight': 'waiting',
  'token in': 'waiting',
  'ready to send': 'waiting',
  new: 'waiting',

  /* Attention — a person has to do something. */
  'needs a landlord': 'attention',
  required: 'attention',
  overdue: 'attention',
  'action needed': 'attention',
  unassigned: 'attention',

  /* Bad — it failed, or it is broken. */
  failed: 'bad',
  breached: 'bad',
  down: 'bad',
  refused: 'bad',
  cancelled: 'bad',
  error: 'bad',

  /* Neutral — a fact with no action attached. */
  'built-in': 'neutral',
  draft: 'neutral',
  archived: 'neutral',
  'in the trash': 'neutral',
  disabled: 'neutral',
  mock: 'neutral',
  off: 'neutral',
};

export function toneFor(status: string): StatusTone {
  return KNOWN[status.trim().toLowerCase()] ?? 'neutral';
}
